// Store module for Serie (Abonnement) creation/editing
// Manages state between steps to minimize DB queries

const state = () => ({
    // Current step tracking
    currentStep: 1,

    // Serie ID (null for create, number for edit)
    serieId: null,

    // Step 1 - Informations générales
    formData: {
        name: '',
        description: '',
        image: null,
        imagePreview: '',
        visible_online: true,
        as_undated_ticket: false,
    },

    // Step 2 - Tarification
    pricing: {
        price: 0,
        price_ht: 0,
        vat_rate: 20,
        code_ean: '',
        prestations_qty: 1,
        is_ticket: true,
        duree: 30, // Default duration (e.g. 1 month)
        monthly_payment: false,
        unlimited: false,
    },

    // Step 3 - Paramètres (heures pleines/creuses)
    parameters: {
        interval_hours_type: 'week', // 'week' or 'month'
        total_peak_hours: null,
        total_off_peak_hours: null,
        nb_peak_hours_per: null,
        nb_off_peak_hours_per: null,
        allow_peak_for_off_peak: false,
        // Toggle states
        toggle_peak_hours: false,
        toggle_off_peak_hours: false,
        toggle_max_peak_hours: false,
        toggle_max_off_peak_hours: false,
    },

    // Step 4 - Activités (prestations)
    seriePrestations: [],

    // Step 5 - Options
    options: {
        visible_online: true,
        as_undated_ticket: false,
    },

    // Available data from backend
    availablePrestations: [],
    countryVatRates: [],

    // Loading and error states
    loading: false,
    errors: {},

    // Track if data has been modified
    isDirty: false,
})

const mutations = {
    SET_CURRENT_STEP(state, step) {
        state.currentStep = step
    },

    SET_SERIE_ID(state, id) {
        state.serieId = id
    },

    SET_FORM_DATA(state, data) {
        state.formData = { ...state.formData, ...data }
        state.isDirty = true
    },

    SET_PRICING(state, data) {
        state.pricing = { ...state.pricing, ...data }
        state.isDirty = true
    },

    SET_PARAMETERS(state, data) {
        state.parameters = { ...state.parameters, ...data }
        state.isDirty = true
    },

    SET_OPTIONS(state, data) {
        state.options = { ...state.options, ...data }
        state.isDirty = true
    },

    SET_SERIE_PRESTATIONS(state, prestations) {
        state.seriePrestations = prestations
        state.isDirty = true
    },

    ADD_SERIE_PRESTATION(state, prestation) {
        state.seriePrestations.push({
            selectedPrestation: null,
            selectedDuration: null,
            quantity: 1,
            ...prestation,
        })
        state.isDirty = true
    },

    UPDATE_SERIE_PRESTATION(state, { index, data }) {
        if (state.seriePrestations[index]) {
            state.seriePrestations.splice(index, 1, { ...state.seriePrestations[index], ...data })
            state.isDirty = true
        }
    },

    REMOVE_SERIE_PRESTATION(state, index) {
        state.seriePrestations.splice(index, 1)
        state.isDirty = true
    },

    SET_AVAILABLE_PRESTATIONS(state, prestations) {
        state.availablePrestations = prestations
    },

    SET_COUNTRY_VAT_RATES(state, rates) {
        state.countryVatRates = rates
    },

    SET_LOADING(state, loading) {
        state.loading = loading
    },

    SET_ERRORS(state, errors) {
        state.errors = errors
    },

    CLEAR_ERROR(state, field) {
        if (state.errors[field]) {
            delete state.errors[field]
        }
    },

    SET_IS_DIRTY(state, isDirty) {
        state.isDirty = isDirty
    },

    // Load serie data from API for edit mode
    LOAD_SERIE(state, serieData) {
        state.serieId = serieData.id

        state.formData = {
            name: serieData.name || '',
            description: serieData.description || '',
            image: serieData.image || null,
            imagePreview: serieData.image || '',
            // Cast to boolean so that 1 / true / '1' all activate the toggle
            visible_online: !!serieData.visible_online,
            as_undated_ticket: !!serieData.as_undated_ticket,
        }

        state.pricing = {
            price: serieData.price || 0,
            price_ht: serieData.price_ht || 0,
            vat_rate: parseFloat(serieData.vat_rate) || 20,
            code_ean: serieData.code_ean || '',
            prestations_qty: serieData.prestations_qty || 1,
            is_ticket: Boolean(serieData.is_ticket),
            duree: serieData.duree || 30,
            monthly_payment: !!serieData.monthly_payment,
            unlimited: !!serieData.unlimited,
        }

        state.parameters = {
            interval_hours_type: serieData.interval_hours_type || 'week',
            total_peak_hours: serieData.total_peak_hours,
            total_off_peak_hours: serieData.total_off_peak_hours,
            nb_peak_hours_per: serieData.nb_peak_hours_per,
            nb_off_peak_hours_per: serieData.nb_off_peak_hours_per,
            allow_peak_for_off_peak: serieData.allow_peak_for_off_peak === 1,
            toggle_peak_hours: serieData.total_peak_hours !== null,
            toggle_off_peak_hours: serieData.total_off_peak_hours !== null,
            toggle_max_peak_hours: serieData.nb_peak_hours_per !== null,
            toggle_max_off_peak_hours: serieData.nb_off_peak_hours_per !== null,
        }

        state.options = {
            // Same cast here so Step5 toggle is in sync with DB
            visible_online: !!serieData.visible_online,
            as_undated_ticket: !!serieData.as_undated_ticket,
        }

        // Load serie prestations if present
        if (serieData.prestations && serieData.prestations.length > 0) {
            state.seriePrestations = serieData.prestations.map((item) => {
                const prestationId = item.prestation_id || item.id
                const foundPrestation = state.availablePrestations.find((p) => p.id == prestationId)

                let selectedDuration = null
                if (foundPrestation) {
                    const durations =
                        foundPrestation.prices_by_times || foundPrestation.pricesByTimes || foundPrestation.durations || []
                    const durationId = item.pivot?.duration_id || item.duration_id
                    selectedDuration = durations.find((d) => d.id == durationId)
                }

                return {
                    selectedPrestation: foundPrestation || item,
                    selectedDuration:
                        selectedDuration ||
                        (item.pivot
                            ? { id: item.pivot.duration_id, duration: item.pivot.duration }
                            : { id: item.duration_id, duration: item.duration }),
                    quantity: item.pivot?.prestation_qty_max || item.pivot?.prestation_qty || item.prestation_qty || 1,
                }
            })
        }

        state.isDirty = false
    },

    RESET_STORE(state) {
        state.currentStep = 1
        state.serieId = null
        state.formData = {
            name: '',
            description: '',
            image: null,
            imagePreview: '',
            visible_online: true,
            as_undated_ticket: false,
        }
        state.pricing = {
            price: 0,
            price_ht: 0,
            vat_rate: 20,
            code_ean: '',
            prestations_qty: 1,
            is_ticket: true,
            duree: 30,
            monthly_payment: false,
            unlimited: false,
        }
        state.parameters = {
            interval_hours_type: 'week',
            total_peak_hours: null,
            total_off_peak_hours: null,
            nb_peak_hours_per: null,
            nb_off_peak_hours_per: null,
            allow_peak_for_off_peak: false,
            toggle_peak_hours: false,
            toggle_off_peak_hours: false,
            toggle_max_peak_hours: false,
            toggle_max_off_peak_hours: false,
        }
        state.options = {
            visible_online: true,
            as_undated_ticket: false,
        }
        state.seriePrestations = []
        state.errors = {}
        state.isDirty = false
    },
}

const actions = {
    setCurrentStep({ commit }, step) {
        commit('SET_CURRENT_STEP', step)
    },

    setFormData({ commit }, data) {
        commit('SET_FORM_DATA', data)
    },

    setPricing({ commit }, data) {
        commit('SET_PRICING', data)
    },

    setParameters({ commit }, data) {
        commit('SET_PARAMETERS', data)
    },

    setOptions({ commit }, data) {
        commit('SET_OPTIONS', data)
    },

    setSeriePrestations({ commit }, prestations) {
        commit('SET_SERIE_PRESTATIONS', prestations)
    },

    addSeriePrestation({ commit }, prestation = {}) {
        commit('ADD_SERIE_PRESTATION', prestation)
    },

    updateSeriePrestation({ commit }, { index, data }) {
        commit('UPDATE_SERIE_PRESTATION', { index, data })
    },

    removeSeriePrestation({ commit }, index) {
        commit('REMOVE_SERIE_PRESTATION', index)
    },

    setAvailablePrestations({ commit }, prestations) {
        commit('SET_AVAILABLE_PRESTATIONS', prestations)
    },

    setCountryVatRates({ commit }, rates) {
        commit('SET_COUNTRY_VAT_RATES', rates)
    },

    setErrors({ commit }, errors) {
        commit('SET_ERRORS', errors)
    },

    clearError({ commit }, field) {
        commit('CLEAR_ERROR', field)
    },

    // Load serie for edit mode
    async loadSerie({ commit }, serieId) {
        commit('SET_LOADING', true)
        try {
            const response = await axios.get(`/api/v3/serie/${serieId}`)
            commit('LOAD_SERIE', response.data.data || response.data)
            return response.data
        } catch (error) {
            console.error('Error loading serie:', error)
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    // Save serie (create or update)
    async saveSerie({ state, commit }) {
        commit('SET_LOADING', true)
        commit('SET_ERRORS', {})

        try {
            const isEditMode = state.serieId !== null

            // Business rules:
            // - In create mode: draft = 1 if step < 5, draft = 0 only when completing step 5
            // - In edit mode: serie is already complete (steps valid), keep draft = 0 by default
            // - visible_online can ONLY be 1 if draft = 0
            // - If draft = 1, visible_online MUST be 0
            const isComplete = isEditMode ? true : state.currentStep >= 5
            const isDraft = !isComplete

            const payload = {
                id: state.serieId,
                name: state.formData.name,
                description: state.formData.description,
                image: state.formData.image,
                archiver: isDraft ? 1 : 0, // Using archiver as draft equivalent if consistent with SerieController
                // visible_online can only be 1 if draft = 0 AND user enabled it
                visible_online: !isDraft && state.options.visible_online ? 1 : 0,
                as_undated_ticket: state.options.as_undated_ticket ? 1 : 0,
                price: state.pricing.price,
                price_ht: state.pricing.price_ht,
                vat_rate: state.pricing.vat_rate,
                code_ean: state.pricing.code_ean,
                prestations_qty: state.pricing.unlimited ? 1000 : state.pricing.prestations_qty,
                unlimited: state.pricing.unlimited ? 1 : 0,
                duree: state.pricing.duree,
                monthly_payment: state.pricing.monthly_payment ? 1 : 0,

                is_ticket: state.pricing.is_ticket ? 1 : 0,
                toggle_peak_hours: state.parameters.toggle_peak_hours,
                toggle_off_peak_hours: state.parameters.toggle_off_peak_hours,
                toggle_max_peak_hours: state.parameters.toggle_max_peak_hours,
                toggle_max_off_peak_hours: state.parameters.toggle_max_off_peak_hours,
                interval_hours_type: state.parameters.interval_hours_type,
                total_peak_hours: state.parameters.toggle_peak_hours ? state.parameters.total_peak_hours : null,
                total_off_peak_hours: state.parameters.toggle_off_peak_hours ? state.parameters.total_off_peak_hours : null,
                nb_peak_hours_per: state.parameters.toggle_max_peak_hours ? state.parameters.nb_peak_hours_per : null,
                nb_off_peak_hours_per: state.parameters.toggle_max_off_peak_hours ? state.parameters.nb_off_peak_hours_per : null,
                allow_peak_for_off_peak: state.parameters.allow_peak_for_off_peak ? 1 : 0,
                // steps reflects the current step in create mode; in edit mode we keep existing steps
                steps: state.currentStep,
                prestations: state.seriePrestations
                    .filter((item) => item.selectedPrestation)
                    .map((item, index) => ({
                        prestation_id: item.selectedPrestation?.id,
                        duration: item.selectedDuration?.id, // Send ID (duration_id in backend)
                        prestation_qty: item.quantity || 1,
                        sort_order: index,
                    })),
            }

            // Mapped to /serie/create store method or api route
            const url = state.serieId ? `/api/v3/serie/${state.serieId}` : '/api/v3/serie'
            const method = state.serieId ? 'put' : 'post'

            const response = await axios[method](url, payload)
            commit('SET_IS_DIRTY', false)

            // Store the serie ID for subsequent saves and update URL
            if (!state.serieId && response.data.data?.id) {
                const newSerieId = response.data.data.id
                commit('SET_SERIE_ID', newSerieId)

                // Update browser URL without reload to enable resume on refresh
                const newUrl = `/back-office/v3/serie/create/${newSerieId}`
                window.history.replaceState({ serieId: newSerieId }, '', newUrl)
            }

            return response.data
        } catch (error) {
            if (error.response?.data?.errors) {
                commit('SET_ERRORS', error.response.data.errors)
            }
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    // Save step progress to database (for resumable creation)
    async saveStepProgress({ state, commit }, stepNumber) {
        commit('SET_CURRENT_STEP', stepNumber)

        // Only save if we have at least step 1 completed (name filled)
        if (!state.formData.name) {
            return
        }

        commit('SET_LOADING', true)
        try {
            const payload = {
                id: state.serieId,
                name: state.formData.name || 'Brouillon - Abonnement',
                description: state.formData.description,
                image: state.formData.image,
                archiver: 1, // Draft
                visible_online: 0, // Not visible until fully created
                as_undated_ticket: state.options.as_undated_ticket ? 1 : 0,
                price: state.pricing.price || 0,
                price_ht: state.pricing.price_ht || 0,
                vat_rate: state.pricing.vat_rate || 20,
                code_ean: state.pricing.code_ean,
                prestations_qty: state.pricing.unlimited ? 1000 : state.pricing.prestations_qty || 1,
                unlimited: state.pricing.unlimited ? 1 : 0,
                duree: state.pricing.duree,
                monthly_payment: state.pricing.monthly_payment ? 1 : 0,

                is_ticket: state.pricing.is_ticket ? 1 : 0,
                interval_hours_type: state.parameters.interval_hours_type,
                toggle_peak_hours: state.parameters.toggle_peak_hours,
                toggle_off_peak_hours: state.parameters.toggle_off_peak_hours,
                toggle_max_peak_hours: state.parameters.toggle_max_peak_hours,
                toggle_max_off_peak_hours: state.parameters.toggle_max_off_peak_hours,
                total_peak_hours: state.parameters.toggle_peak_hours ? state.parameters.total_peak_hours : null,
                total_off_peak_hours: state.parameters.toggle_off_peak_hours ? state.parameters.total_off_peak_hours : null,
                nb_peak_hours_per: state.parameters.toggle_max_peak_hours ? state.parameters.nb_peak_hours_per : null,
                nb_off_peak_hours_per: state.parameters.toggle_max_off_peak_hours ? state.parameters.nb_off_peak_hours_per : null,
                allow_peak_for_off_peak: state.parameters.allow_peak_for_off_peak ? 1 : 0,
                steps: stepNumber,
                prestations: state.seriePrestations
                    .filter((item) => item.selectedPrestation)
                    .map((item, index) => ({
                        prestation_id: item.selectedPrestation?.id,
                        duration: item.selectedDuration?.id, // Send ID
                        prestation_qty: item.quantity || 1,
                        sort_order: index,
                    })),
            }

            const url = state.serieId ? `/api/v3/serie/${state.serieId}` : '/api/v3/serie'
            const method = state.serieId ? 'put' : 'post'

            const response = await axios[method](url, payload)
            commit('SET_IS_DIRTY', false)

            // Store the serie ID for subsequent saves and update URL
            if (!state.serieId && response.data.data?.id) {
                const newSerieId = response.data.data.id
                commit('SET_SERIE_ID', newSerieId)

                // Update browser URL without reload to enable resume on refresh
                const newUrl = `/back-office/v3/serie/create/${newSerieId}`
                window.history.replaceState({ serieId: newSerieId }, '', newUrl)
            }

            return response.data
        } catch (error) {
            console.error('Error saving step progress:', error)
            // Don't throw - allow user to continue even if save fails
        } finally {
            commit('SET_LOADING', false)
        }
    },

    resetStore({ commit }) {
        commit('RESET_STORE')
    },
}

const getters = {
    currentStep: (state) => state.currentStep,
    serieId: (state) => state.serieId,
    isEditMode: (state) => state.serieId !== null,
    formData: (state) => state.formData,
    pricing: (state) => state.pricing,
    parameters: (state) => state.parameters,
    options: (state) => state.options,
    seriePrestations: (state) => state.seriePrestations,
    availablePrestations: (state) => state.availablePrestations,
    countryVatRates: (state) => state.countryVatRates,
    loading: (state) => state.loading,
    errors: (state) => state.errors,
    isDirty: (state) => state.isDirty,

    // Get available prestations excluding already selected ones
    getAvailablePrestations: (state) => (currentIndex) => {
        const selectedIds = state.seriePrestations
            .filter((_, index) => index !== currentIndex)
            .map((item) => item.selectedPrestation?.id)
            .filter((id) => id !== undefined)

        return state.availablePrestations.filter((prestation) => !selectedIds.includes(prestation.id))
    },

    // Validation state for each step
    isStep1Valid: (state) => {
        return state.formData.name && state.formData.name.trim() !== ''
    },

    isStep2Valid: (state) => {
        // At least one prestation should be selected
        return (
            state.seriePrestations.length > 0 &&
            state.seriePrestations.every((item) => item.selectedPrestation && item.selectedDuration)
        )
    },

    isStep3Valid: (state) => {
        return state.pricing.price !== null && state.pricing.vat_rate !== null && state.pricing.duree
    },

    isStep4Valid: (state) => {
        // Options step is always valid (optional settings)
        return true
    },

    isStep5Valid: (state) => {
        // Options step is always valid
        return true
    },

    // Get step validation status
    getStepValidation: (state, getters) => (stepId) => {
        switch (stepId) {
            case 1:
                return getters.isStep1Valid
            case 2:
                return getters.isStep2Valid
            case 3:
                return getters.isStep3Valid
            case 4:
                return getters.isStep4Valid
            case 5:
                return getters.isStep5Valid
            default:
                return false
        }
    },

    // Computed total duration from prestations
    totalDuration: (state) => {
        return state.seriePrestations.reduce((total, item) => {
            const duration = item.selectedDuration?.duration || 0
            const qty = item.quantity || 1
            return total + duration * qty
        }, 0)
    },
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}
